/* eslint-disable consistent-return */
import React, { createContext, useContext, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery } from "@apollo/client";
import { CallBackProps, STATUS } from "react-joyride";
import { useNavigate } from "react-router-dom";

import Tour from "src/components/shared/molecules/Tour/Tour";
import {
  WELCOME_TOUR,
  POST_FB_CONNECTION_TOUR,
  SETTINGS_TOUR,
} from "src/config/tourSteps";
import GET_USER_GUIDE from "src/graphql/query/user_guide/getUserGuide";
import UPDATE_USER_GUIDE from "src/graphql/mutation/user_guide/updateUserGuide";

type ResumeTarget = "chatScreen" | "users" | null;

interface TourContextType {
  run: boolean;
  startTour: () => void;
  stopTour: () => void;
  postFbConnectionTourRun: boolean;
  startPostFbConnectionTour: () => void;
  stopPostFbConnectionTour: () => void;
  userManagementTourRun: boolean;
  startUserManagementTour: () => void;
  stopUserManagementTour: () => void;
  dashboardTourRun: boolean;
  startDashboardTour: () => void;
  stopDashboardTour: () => void;
  reportsTourRun: boolean;
  startReportsTour: () => void;
  stopReportsTour: () => void;
  chatScreenTourRun: boolean;
  startChatScreenTour: () => void;
  stopChatScreenTour: () => void;
  settingsTourRun: boolean;
  startSettingsTour: () => void;
  stopSettingsTour: () => void;

  resumeTarget: ResumeTarget;
  setResumeTarget: React.Dispatch<React.SetStateAction<ResumeTarget>>;

  guideData: any;

  guideLoading: boolean;
  postFbConnectionStepIndex: number;
  setPostFbConnectionStepIndex: (index: number) => void;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [run, setRun] = useState(false);
  const [postFbConnectionTourRun, setPostFbConnectionTourRun] = useState(false);
  const [userManagementTourRun, setUserManagementTourRun] = useState(false);
  const [dashboardTourRun, setDashboardTourRun] = useState(false);
  const [reportsTourRun, setReportsTourRun] = useState(false);
  const [chatScreenTourRun, setChatScreenTourRun] = useState(false);
  const [settingsTourRun, setSettingsTourRun] = useState(false);
  const [postFbConnectionStepIndex, setPostFbConnectionStepIndex] = useState(0);
  //
  const [resumeTarget, setResumeTarget] = useState<ResumeTarget>(null);
  const { me }: any = useSelector((state: any) => state.me);
  const navigate = useNavigate();

  // Force Joyride to recalculate positions after layout settles
  React.useEffect(() => {
    if (postFbConnectionTourRun) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [postFbConnectionTourRun]);

  const { data: guideData, loading: guideLoading } = useQuery(GET_USER_GUIDE, {
    variables: { agentId: me?.id },
    skip: !me?.id,
  });

  // welcome tour started
  // useEffect(() => {
  //   // Only trigger if user is logged in and guide data is loaded
  //   if (!me?.id || !guideData) return;

  //   const guide = guideData.getUserGuide;

  //   // Run tour if welcome tour is incomplete or guide record doesn't exist
  //   if (!guide || guide.welcomeTourCompleted === false) {
  //     const timer = setTimeout(() => {
  //       setRun(true);
  //     }, 1000);

  //     // Cleanup in case component unmounts before timeout fires
  //     return () => clearTimeout(timer);
  //   }
  // }, [me?.id, guideData]);

  const [updateUserGuide] = useMutation(UPDATE_USER_GUIDE);

  const startTour = () => setRun(true);
  const stopTour = () => setRun(false);
  const startPostFbConnectionTour = () => setPostFbConnectionTourRun(true);
  const stopPostFbConnectionTour = () => setPostFbConnectionTourRun(false);

  const handleCallback = async (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(status)) {
      setRun(false);

      // Navigate immediately on FINISHED
      if (status === STATUS.FINISHED) {
        navigate("/settings/facebook");
      }

      try {
        if (me?.id) {
          // Update backend
          await updateUserGuide({
            variables: {
              agentId: me.id,
              welcomeTourCompleted: true,
            },
            refetchQueries: [
              { query: GET_USER_GUIDE, variables: { agentId: me.id } },
            ],
          });
        }
      } catch (error) {
        console.error("Failed to mark tour as completed:", error);
      }
    }
  };

  const handlePostFbConnectionCallback = async (data: CallBackProps) => {
    const { status, action, index, type } = data;

    // Synchronize local step index only when a step is completed manually
    if (type === "step:after" && (action === "next" || action === "prev")) {
      setPostFbConnectionStepIndex(index + (action === "next" ? 1 : -1));
    }

    // Handle tour completion
    if (type === "tour:end") {
      if (status === STATUS.FINISHED && me?.id) {
        setPostFbConnectionTourRun(false);
        // Only navigate if we are actually at the end of the steps
        if (index === POST_FB_CONNECTION_TOUR.length - 1) {
          navigate("/users");
          try {
            await updateUserGuide({
              variables: {
                agentId: me.id,
                postFbConnectionCompleted: true,
              },
              refetchQueries: [
                { query: GET_USER_GUIDE, variables: { agentId: me.id } },
              ],
            });
          } catch (error) {
            console.error("Failed to mark channel setup as completed:", error);
          }
        }
      } else if (status === STATUS.SKIPPED) {
        setPostFbConnectionTourRun(false);
      }
    }
  };

  const handleSettingsTourCallback = async (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(status) && me?.id) {
      setSettingsTourRun(false);
      try {
        await updateUserGuide({
          variables: {
            agentId: me.id,
            settingsTourCompleted: true,
          },
          refetchQueries: [
            { query: GET_USER_GUIDE, variables: { agentId: me.id } },
          ],
        });
      } catch (error) {
        console.error("Failed to mark settings tour as completed:", error);
      }
    }
  };

  /* USER_MANAGEMENT_TOUR LOGIC REMOVED FROM HERE TO UsersMainScreen */

  const contextValue = React.useMemo(
    () => ({
      run,
      startTour,
      stopTour,
      postFbConnectionTourRun,
      startPostFbConnectionTour,
      stopPostFbConnectionTour,
      userManagementTourRun,
      startUserManagementTour: () => setUserManagementTourRun(true),
      stopUserManagementTour: () => setUserManagementTourRun(false),
      dashboardTourRun,
      startDashboardTour: () => setDashboardTourRun(true),
      stopDashboardTour: () => setDashboardTourRun(false),
      reportsTourRun,
      startReportsTour: () => setReportsTourRun(true),
      stopReportsTour: () => setReportsTourRun(false),
      chatScreenTourRun,
      startChatScreenTour: () => setChatScreenTourRun(true),
      stopChatScreenTour: () => setChatScreenTourRun(false),
      settingsTourRun,
      startSettingsTour: () => setSettingsTourRun(true),
      stopSettingsTour: () => setSettingsTourRun(false),

      resumeTarget,
      setResumeTarget,

      guideData,

      guideLoading,
      postFbConnectionStepIndex,
      setPostFbConnectionStepIndex,
    }),
    [
      run,
      postFbConnectionTourRun,
      userManagementTourRun,
      dashboardTourRun,
      reportsTourRun,
      chatScreenTourRun,
      settingsTourRun,
      guideData,
      resumeTarget,
      guideLoading,
      postFbConnectionStepIndex,
    ],
  );

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      <Tour steps={WELCOME_TOUR} run={run} callback={handleCallback} />
      <Tour
        steps={POST_FB_CONNECTION_TOUR}
        run={postFbConnectionTourRun}
        stepIndex={postFbConnectionStepIndex}
        callback={handlePostFbConnectionCallback}
      />
      <Tour
        steps={SETTINGS_TOUR}
        run={settingsTourRun}
        callback={handleSettingsTourCallback}
      />
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
};
